import {useTabsContext} from '@chakra-ui/react/tabs'
import {Button, ButtonProps} from '@chakra-ui/react'
import {LuChevronRight} from 'react-icons/lu'

/**
 * A button for navigating to the next tab in a multi-step form.
 *
 * - Validates the current tab before advancing: if there are invalid fields,
 *   the first one is focused and the tab does not change
 * - If no validation errors, advances to the next tab
 * - Automatically hides on the last tab (no next tab to go to)
 * - Returns null if used outside a `<Tabs.Root>` context
 *
 * Usage:
 *   <Tabs.Root value={tab} onValueChange={setTab}>
 *     <Tabs.List>...</Tabs.List>
 *     <Tabs.Content>...</Tabs.Content>
 *     <form.NextStepButton>Next step</form.NextStepButton>
 *   </Tabs.Root>
 */

function hasCurrentTabErrors(activeTab: string): HTMLElement | null {
  const currentPanel = document.querySelector(`[role="tabpanel"][aria-labelledby$="trigger-${activeTab}"]`)

  if (!currentPanel) return null

  return currentPanel.querySelector('[aria-invalid="true"]') as HTMLElement
}

function getNextTabValue(activeTab: string): string | undefined {
  const tabList = document.querySelector('[role="tablist"]')
  if (!tabList) return undefined

  const triggers = tabList.querySelectorAll('[role="tab"]')
  let found = false

  for (const trigger of triggers) {
    if (found) return trigger.getAttribute('data-value') || undefined
    if (trigger.getAttribute('data-value') === activeTab) found = true
  }

  return undefined
}

export function NextStepButton(props: ButtonProps) {
  let tabs: ReturnType<typeof useTabsContext>

  try {
    tabs = useTabsContext()
  } catch {
    return null
  }

  const nextTabValue = getNextTabValue(tabs.value ?? '')
  if (!nextTabValue) return null

  function handleNextStep() {
    const invalidField = hasCurrentTabErrors(tabs.value ?? '')
    if (invalidField) {
      invalidField.focus()
      return
    }

    if (nextTabValue) tabs.setValue(nextTabValue)
  }

  return (
    <Button variant="subtle" colorPalette="cyan" onClick={handleNextStep} {...props}>
      {props.children}
      <LuChevronRight />
    </Button>
  )
}
